import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {import('@shopify/events').ProductSelectEvent} ProductSelectEvent
 */

/** @type {Map<string, string>} */
const selectedTabBySection = new Map();

/**
 * Re-renders Product Info Tabs when the PDP variant changes so packet size
 * and shipping surcharge stay in sync without a full page reload.
 *
 * @extends {Component}
 */
class UfProductInfoTabs extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.addEventListener('click', this.#onTabClick);
    this.addEventListener('keydown', this.#onTabKeydown);
    this.restoreSelectedTab();
    requestAnimationFrame(() => this.#scrollSelectedTabIntoView());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.removeEventListener('click', this.#onTabClick);
    this.removeEventListener('keydown', this.#onTabKeydown);
  }

  rememberSelectedTab() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const tab = this.querySelector('.uf-pit__tabs [role="tab"][aria-selected="true"]');
    const label = this.#tabLabel(tab);
    if (label) selectedTabBySection.set(sectionId, label);
  }

  restoreSelectedTab() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const label = selectedTabBySection.get(sectionId);
    if (!label) return;

    const tabs = this.querySelectorAll('.uf-pit__tabs [role="tab"]');

    for (const tab of tabs) {
      if (this.#tabLabel(tab) !== label) continue;
      if (tab.getAttribute('aria-selected') !== 'true') {
        tab.click();
      }
      this.#scrollSelectedTabIntoView();
      return;
    }
  }

  /**
   * Center the selected tab in the mobile tab carousel so neighbors peek.
   * @param {ScrollBehavior} [behavior]
   */
  #scrollSelectedTabIntoView(behavior = 'auto') {
    const list = this.querySelector('.uf-pit__tabs .tabs__list');
    const tab = this.querySelector('.uf-pit__tabs [role="tab"][aria-selected="true"]');
    if (!(list instanceof HTMLElement) || !(tab instanceof HTMLElement)) return;
    if (list.scrollWidth <= list.clientWidth + 1) return;

    const listRect = list.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const nextLeft =
      list.scrollLeft + (tabRect.left - listRect.left) - (list.clientWidth - tab.offsetWidth) / 2;

    list.scrollTo({ left: Math.max(0, nextLeft), behavior });
  }

  /**
   * @param {MouseEvent} event
   */
  #onTabClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.uf-pit__tabs [role="tab"]')) return;

    requestAnimationFrame(() => this.#scrollSelectedTabIntoView('smooth'));
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onTabKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End') {
      return;
    }
    if (!(event.target instanceof Element) || !event.target.closest('[role="tab"]')) return;

    requestAnimationFrame(() => this.#scrollSelectedTabIntoView('smooth'));
  };

  /**
   * @param {Element | null} tab
   * @returns {string}
   */
  #tabLabel(tab) {
    if (!tab) return '';

    const label = tab.querySelector('.tabs__label');
    return (label?.textContent || tab.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card') || event.target.closest('quick-add-dialog')) return;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.rememberSelectedTab();

    try {
      const { detail } = await event.promise;
      if (!detail) return;

      if (detail.newProduct) {
        this.dataset.productId = detail.newProduct.id;
      } else if (detail.productId && detail.productId !== this.dataset.productId) {
        return;
      }

      const variantId = detail.resource?.id ? String(detail.resource.id) : '';
      if (variantId && variantId === this.dataset.variantId) return;

      const url = new URL(window.location.href);

      if (detail.newProduct?.url) {
        url.pathname = new URL(detail.newProduct.url, window.location.origin).pathname;
      }

      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }

      await sectionRenderer.renderSection(sectionId, { cache: false, url });

      const live = document.querySelector(`uf-product-info-tabs[data-section-id="${CSS.escape(sectionId)}"]`);
      if (live instanceof UfProductInfoTabs) {
        live.restoreSelectedTab();
      }

      const focusedVariant = variantId
        ? document.querySelector(`variant-picker input[data-variant-id="${CSS.escape(variantId)}"]:checked`)
        : null;
      if (focusedVariant instanceof HTMLElement) {
        focusedVariant.focus({ preventScroll: true });
      }

      document.dispatchEvent(new CustomEvent('uf-zone:updated'));
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[uf-product-info-tabs] Variant refresh failed:', error);
      }
    }
  };
}

if (!customElements.get('uf-product-info-tabs')) {
  customElements.define('uf-product-info-tabs', UfProductInfoTabs);
}
